import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { JSX } from 'react';
import { type ForwardedRef, type ReactNode, forwardRef, memo, useMemo } from 'react';

type inputType =
  'date' | 'datetime-local' | 'email' | 'number' | 'tel' | 'text' | 'time' | 'url' | 'password';

export interface Props {
  appendedText?: string;
  autoComplete?: string;
  disabled?: boolean;
  helpText?: string;
  label?: string | ReactNode;
  max?: number | string;
  maxLength?: number;
  min?: number | string;
  id: string;
  inputClassName?: string;
  inputMode?:
    'decimal' | 'email' | 'none' | 'numeric' | 'search' | 'tel' | 'text' | 'url' | undefined;
  onBlur?: () => void;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  pattern?: string;
  placeholder?: string;
  readOnly?: boolean;
  requiredField?: ReactNode | null;
  step?: number;
  type: inputType;
  typeHelper?: 'dollars' | 'percentage' | 'search';
  value: string | undefined;
  wrapperClassNames?: string;
}

/**
 * @deprecated Do not use this component. Use the base components at the root of the UI folder instead.
 */
const InputField = forwardRef(function Input(
  {
    appendedText,
    autoComplete,
    disabled = false,
    helpText,
    label,
    max,
    maxLength,
    min,
    id,
    inputClassName,
    inputMode,
    onBlur,
    onChange,
    pattern,
    placeholder,
    readOnly = false,
    requiredField,
    step,
    type,
    typeHelper,
    value,
    wrapperClassNames
  }: Props,
  inputRef: ForwardedRef<HTMLInputElement>
) {
  const disabledClassNames = disabled || readOnly ? 'opacity-50 cursor-not-allowed' : '';

  const getTypeHelper = useMemo((): string | JSX.Element | null => {
    if (typeHelper === 'dollars') {
      return '$';
    }

    if (typeHelper === 'percentage') {
      return '%';
    }

    if (typeHelper === 'search') {
      return <FontAwesomeIcon className="mr-1" icon={faSearch} />;
    }

    return null;
  }, [typeHelper]);

  return (
    <div>
      {label && (
        <label htmlFor={id} className="block font-medium text-gray-700">
          {label}
        </label>
      )}

      {helpText && <p className="text-sm text-gray-600">{helpText}</p>}

      <div className="relative flex items-center">
        <div className={wrapperClassNames || ''}>
          {typeHelper && (
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <span className="text-gray-500 sm:text-sm">{getTypeHelper}</span>
            </div>
          )}

          <input
            autoComplete={autoComplete}
            className={`my-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-xs focus:outline-hidden focus:ring-indigo-500 focus:border-indigo-500 text-base ${disabledClassNames} ${inputClassName || ''}`}
            disabled={disabled}
            id={id}
            inputMode={inputMode}
            maxLength={maxLength}
            max={max}
            min={min}
            onBlur={onBlur}
            onChange={onChange}
            pattern={pattern}
            placeholder={placeholder}
            readOnly={readOnly}
            ref={inputRef}
            step={step}
            style={
              typeHelper
                ? {
                    paddingLeft: ['percentage', 'search'].includes(typeHelper)
                      ? '1.75rem'
                      : '1.5rem'
                  }
                : {}
            }
            type={type}
            value={value}
          />
        </div>

        {appendedText && <span className="ml-2">{appendedText}</span>}
      </div>

      {requiredField}
    </div>
  );
});

export default memo(InputField);
